<template>
  <v-container id="data-tables" tag="section" fluid>
    <base-material-card color="primary" icon="mdi-relation-many-to-many" inline class="ml-0 px-5 py-4">
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">Conciliação</div>
      </template>

      <v-btn
        id="btn-nova-conciliacao"
        color="success"
        dark
        elevation="1"
        class="mt-5"
        style="float: left"
        relative
        text
        medium
        @click="$router.push('edit')"
      >
        <v-icon left size="30px">mdi-plus-circle</v-icon>
        NOVA CONCILIAÇÃO
      </v-btn>

      <v-text-field
        id="conciliacao-historico-search"
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search ml-auto mt-4"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 250px"
      />

      <v-divider class="mt-10" />

      <v-data-table
        class="conciliacao-table"
        :headers="headers"
        :items="items"
        :search.sync="search"
        :header-props="{ sortByText: 'Ordenar Por' }"
        :footer-props="{ itemsPerPageText: 'Itens por Página' }"
        no-data-text="Nenhuma conciliação disponível"
        no-results-text="Nenhum registro correspondente encontrado"
        mobile-breakpoint="800"
        :loading="loading"
      >
        <template v-slot:[`item.nome`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div
                class="text-truncate-column"
                :class="podeAcompanhar(item) ? 'clickable-item' : ''"
                v-bind="attrs"
                v-on="on"
                @click="abrirAcompanhamento(item)"
              >
                {{ item.nome }}
              </div>
            </template>
            <span>{{ item.nome }}</span>
          </v-tooltip>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <general-status :status="item.status" />
        </template>

        <template v-slot:[`item.progresso`]="{ item }">
          <template v-if="podeAcompanhar(item)">
            <v-tooltip v-if="progresso[item.id]" bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="progresso-conciliacao" v-bind="attrs" v-on="on">
                  <v-progress-linear :value="progresso[item.id].percentual" height="16" rounded color="primary lighten-2">
                    <small class="font-weight-medium">{{ formatarPercentual(progresso[item.id].percentual) }}</small>
                  </v-progress-linear>
                  <div class="caption grey--text text--darken-1 mt-1">{{ resumoProgresso(progresso[item.id]) }}</div>
                </div>
              </template>
              <div>Pares conciliados: {{ paresConciliados(progresso[item.id]) }}</div>
              <div>Tempo decorrido: {{ formatarDuracao(progresso[item.id].tempoDecorridoMs) }}</div>
            </v-tooltip>
            <span v-else class="caption grey--text">Aguardando início</span>
          </template>
          <span v-else>-</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            id="action-download-conciliacao"
            :class="item.status !== 'SUCESSO' ? 'cursor-block' : ''"
            min-width="0"
            class="px-1"
            fab
            icon
            x-small
            :loading="downloadId === item.id"
            :disabled="item.status !== 'SUCESSO'"
            @click="baixarResultado(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>

          <v-btn
            id="action-edit-conciliacao"
            :class="!canEdit(item) ? 'cursor-block' : ''"
            :disabled="!canEdit(item)"
            min-width="0"
            class="ml-n1 px-1"
            fab
            icon
            x-small
            @click="$router.push(`edit/${item.id}`)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>

          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="action-more-conciliacao"
                color="white"
                class="ml-1"
                height="22px"
                width="22px"
                fab
                x-small
                elevation="1"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canRun(item)" @click="executar(item.id)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-play</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Executar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDownloadLog(item)" @click="baixarLog(item)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-file-download-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Baixar log</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openDialogDetalhes(item)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-file-find-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Detalhes</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDelete(item)" @click="openDialogDelete(item)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Deletar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:[`footer`]>
          <v-switch
            v-if="$store.getters.userIsAdmin"
            class="pa-0 pl-2 swithInterna"
            style="position: absolute"
            color="primary"
            label="Exibir Expirados"
            dense
            @change="$emit('switchProcessamentosExpirados', $event)"
          />
        </template>

        <template v-slot:[`footer.page-text`]>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" @click="$emit('rechargeTable')">mdi-refresh</v-icon>
            </template>
            <span>Clique aqui para recarregar os registros de Conciliação</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </base-material-card>

    <dialog-delete
      :show-dialog="dialogDelete"
      @changeValue="dialogDelete = $event"
      @deleteTriggered="excluir(dialogDeleteData.id)"
    >
      <template slot="title">Deletar Conciliação #{{ dialogDeleteData.id }}</template>
      <template slot="body">
        Tem certeza que deseja deletar a conciliação
        <strong>{{ dialogDeleteData.titulo }}</strong>
        ?
      </template>
    </dialog-delete>

    <general-dialog-detalhes title="Detalhes da Conciliação" :show-dialog="dialogDetalhes" @changeValue="dialogDetalhes = $event">
      <template slot="body">
        <v-data-table
          :headers="[
            { text: 'Parâmetro', value: 'parametro', sortable: false },
            { text: 'Valor', value: 'valor', sortable: false }
          ]"
          :items="dialogDetalhesData"
          hide-default-footer
          class="elevation-1 style-table-dialog-detalhes"
          disable-pagination
        />
        <template v-if="dialogDetalhesMetricas.length">
          <div class="subtitle-2 mt-6 mb-2">Métricas por rodada</div>
          <v-data-table
            :headers="headersMetricas"
            :items="dialogDetalhesMetricas"
            hide-default-footer
            class="elevation-1 style-table-dialog-detalhes"
            disable-pagination
            dense
          />
        </template>
      </template>
    </general-dialog-detalhes>

    <general-dialog-acompanhamento
      ref="dialogAcompanhamento"
      :show-dialog="dialogAcompanhamento"
      :messages="dialogAcompanhamentoData.andamento"
      @changeValue="dialogAcompanhamento = $event"
    >
      <template slot="title">Andamento da Conciliação #{{ dialogAcompanhamentoData.id }}</template>
    </general-dialog-acompanhamento>
  </v-container>
</template>

<script>
  import ConciliacaoService from '@/services/ConciliacaoService';

  const INTERVALO_ANDAMENTO_MS = 3000;

  export default {
    name: 'ConciliacaoTabela',
    components: {
      DialogDelete: () => import('@/components/general/DialogDelete'),
      GeneralStatus: () => import('@/components/general/GeneralStatus'),
      GeneralDialogDetalhes: () => import('@/components/general/GeneralDialogDetalhes'),
      GeneralDialogAcompanhamento: () => import('@/components/general/GeneralDialogAcompanhamento')
    },
    props: {
      items: { type: Array, default: () => [] },
      progresso: { type: Object, default: () => ({}) },
      loading: { type: Boolean, default: false }
    },
    data: () => ({
      search: '',
      downloadId: null,
      dialogDelete: false,
      dialogDeleteData: { id: null, titulo: '' },
      dialogDetalhes: false,
      dialogDetalhesData: [],
      dialogDetalhesMetricas: [],
      headersMetricas: [
        { text: 'Rodada', value: 'rodada', sortable: false },
        { text: 'Pares conciliados', value: 'conciliados', sortable: false },
        { text: 'Remanescentes (linhas P + R)', value: 'remanescentes', sortable: false },
        { text: 'Tempo', value: 'tempo', sortable: false }
      ],
      dialogAcompanhamento: false,
      dialogAcompanhamentoData: { id: undefined, andamento: [] },
      pollingAndamento: null,
      headers: [
        { text: '#', value: 'id' },
        { text: 'Nome', value: 'nome' },
        { text: 'Usuário', value: 'usuario' },
        { text: 'Executado', value: 'executed' },
        { text: 'Status', value: 'status' },
        { sortable: false, text: 'Progresso', value: 'progresso', width: '200px' },
        { sortable: false, text: 'Ações', value: 'actions', class: 'pl-4' }
      ]
    }),
    watch: {
      dialogAcompanhamento(aberto) {
        if (!aberto) {
          this.pararPollingAndamento();
        }
      }
    },
    beforeDestroy() {
      this.pararPollingAndamento();
    },
    methods: {
      podeAcompanhar(item) {
        return ['AGUARDANDO', 'EXECUTANDO'].includes(item.status);
      },
      canDownloadLog(item) {
        return ['SUCESSO', 'FALHA'].includes(item.status);
      },
      abrirAcompanhamento(item) {
        if (!this.podeAcompanhar(item)) {
          return;
        }

        this.dialogAcompanhamentoData = { id: item.id, andamento: [] };
        this.dialogAcompanhamento = true;
        this.carregarAndamento(item.id);
        this.iniciarPollingAndamento(item.id);
      },
      iniciarPollingAndamento(id) {
        this.pararPollingAndamento();
        this.pollingAndamento = setInterval(() => {
          this.carregarAndamento(id, { exibirToastErro: false });
          const item = this.items.find((i) => i.id === id);
          if (!item || !this.podeAcompanhar(item)) {
            this.pararPollingAndamento();
          }
        }, INTERVALO_ANDAMENTO_MS);
      },
      pararPollingAndamento() {
        if (this.pollingAndamento) {
          clearInterval(this.pollingAndamento);
          this.pollingAndamento = null;
        }
      },
      carregarAndamento(id, { exibirToastErro = true } = {}) {
        ConciliacaoService.andamento(id)
          .then(({ data }) => {
            this.dialogAcompanhamentoData.andamento = data?.data?.andamento || [];
            if (this.$refs.dialogAcompanhamento) {
              this.$refs.dialogAcompanhamento.markLoaded();
            }
          })
          .catch((err) => {
            console.error(err);
            if (exibirToastErro) {
              this.$toast.error('Erro ao recuperar andamento da conciliação.', '', { position: 'topRight' });
            }
          });
      },
      formatarPercentual(percentual) {
        return `${Math.floor(Number(percentual) || 0)}%`;
      },
      formatarDuracao(ms) {
        if (ms == null) return '-';
        const total = Math.round(ms / 1000);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        if (h) return `${h}h ${String(m).padStart(2, '0')}min`;
        if (m) return `${m}min ${String(s).padStart(2, '0')}s`;
        return `${s}s`;
      },
      paresConciliados(progresso) {
        return Math.floor((progresso?.registrosConciliados || 0) / 2).toLocaleString('pt-BR');
      },
      resumoProgresso(progresso) {
        const partes = [];
        if (progresso.totalRodadas) {
          partes.push(`${progresso.rodadaAtual || 0}/${progresso.totalRodadas} rodadas`);
        }
        if (progresso.tempoEstimadoRestanteMs != null) {
          partes.push(`~${this.formatarDuracao(progresso.tempoEstimadoRestanteMs)} restante`);
        }
        return partes.join(' · ');
      },
      canEdit(item) {
        return !['EXECUTANDO', 'AGUARDANDO'].includes(item.status);
      },
      canRun(item) {
        return ['SUCESSO', 'FALHA', 'EXPIRADO'].includes(item.status);
      },
      canDelete(item) {
        return !['EXECUTANDO', 'AGUARDANDO'].includes(item.status);
      },
      openDialogDelete(item) {
        this.dialogDeleteData = { id: item.id, titulo: item.nome };
        this.dialogDelete = true;
      },
      openDialogDetalhes(item) {
        this.dialogDetalhesData = this.linhasDetalhes(item);
        this.dialogDetalhesMetricas = [];
        this.dialogDetalhes = true;
        if (item.status === 'SUCESSO') {
          this.carregarMetricas(item.id);
        }
        ConciliacaoService.getEntity(item.id)
          .then(({ data }) => {
            this.dialogDetalhesData = this.linhasDetalhes(item, data?.data?.parametros);
          })
          .catch(() => {
            this.$toast.error('Não foi possível carregar flexibilidade e coringa.', '', {
              position: 'topRight'
            });
          });
      },
      carregarMetricas(id) {
        ConciliacaoService.metricasExecucao(id)
          .then(({ data }) => {
            this.dialogDetalhesMetricas = (data?.data?.metricasPorRodada || []).map((m) => ({
              rodada: m.sequencia,
              conciliados: Number(m.conciliado || 0).toLocaleString('pt-BR'),
              remanescentes: Number(m.remanescente || 0).toLocaleString('pt-BR'),
              tempo: this.formatarDuracao(m.tempoMs)
            }));
          })
          .catch(() => {
            this.$toast.error('Não foi possível carregar as métricas por rodada.', '', { position: 'topRight' });
          });
      },
      linhasDetalhes(item, parametros = {}) {
        const flex = parametros.flexibilizacao || {};
        const coringa = parametros.coringa || {};
        const coringaHabilitado = !!coringa.habilitado;

        return [
          { parametro: 'NOME:', valor: item.nome },
          { parametro: 'TIPO:', valor: item.tipo || '-' },
          { parametro: 'FLEXIBILIZAÇÃO PARTIDA:', valor: flex.partida || '-' },
          { parametro: 'FLEXIBILIZAÇÃO REFERÊNCIA:', valor: flex.referencia || '-' },
          { parametro: 'CORINGA:', valor: coringaHabilitado ? 'Sim' : 'Não' },
          { parametro: 'NÍVEL DO CORINGA:', valor: coringaHabilitado ? coringa.nivelCoringa || '-' : '-' },
          { parametro: 'USUÁRIO:', valor: item.usuario || '-' },
          { parametro: 'CRIADO:', valor: item.created || '-' },
          { parametro: 'EXECUTADO:', valor: item.executed || '-' },
          { parametro: 'STATUS:', valor: item.status || '-' },
          { parametro: 'VERSÃO:', valor: item.versao != null ? item.versao : '-' }
        ];
      },
      baixarLog(item) {
        if (!this.canDownloadLog(item)) return;
        ConciliacaoService.baixarLog(item.id)
          .then((res) => {
            this.$toast.success('Preparando o arquivo de log para download.', '', { position: 'topRight' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `conciliacao-v3-${item.id}.log`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          })
          .catch(() => {
            this.$toast.error('Falha ao baixar o log da conciliação.', '', { position: 'topRight' });
          });
      },
      baixarResultado(item) {
        if (item.status !== 'SUCESSO') return;
        this.downloadId = item.id;
        ConciliacaoService.baixar(item.id)
          .then(({ data }) => {
            const url = data?.data?.url;
            const nome = data?.data?.nome;
            if (!url) {
              throw new Error('URL do resultado não foi retornada.');
            }
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nome);
            link.rel = 'noopener';
            document.body.appendChild(link);
            link.click();
            link.remove();
          })
          .catch((err) => {
            const msg = err?.response?.data?.message || err?.message || 'Falha no download do resultado.';
            this.$toast.error(msg, '', { position: 'topRight' });
          })
          .finally(() => {
            this.downloadId = null;
          });
      },
      executar(id) {
        ConciliacaoService.executar(id)
          .then(({ data }) => {
            this.$toast.success(data?.message || 'Execução solicitada.', '', { position: 'topRight' });
            this.$emit('rechargeTable');
          })
          .catch((err) => {
            const msg = err?.response?.data?.message || 'Erro ao executar.';
            this.$toast.error(msg, '', { position: 'topRight' });
          });
      },
      excluir(id) {
        ConciliacaoService.excluir(id)
          .then(() => {
            this.dialogDelete = false;
            this.$toast.success('Conciliação removida.', '', { position: 'topRight' });
            this.$emit('rechargeTable');
          })
          .catch((err) => {
            const msg = err?.response?.data?.message || 'Erro ao excluir.';
            this.$toast.error(msg, '', { position: 'topRight' });
          });
      }
    }
  };
</script>

<style>
  .swithInterna label {
    font-size: 13px !important;
    margin-left: 0px !important;
  }
</style>
